import React, { useCallback, useEffect, useMemo, useState } from "react";
import { X } from "lucide-react";
import { useTranslation } from "../i18n";
import { apiUrl } from "../api";
import LanguageSwitcher from "./LanguageSwitcher";
import "./GalleryModeration.css";

const ADMIN_PASSWORD_KEY = "galleryAdminPassword";

function GalleryModeration() {
  const { t } = useTranslation();
  const [password, setPassword] = useState(() => localStorage.getItem(ADMIN_PASSWORD_KEY) || "");
  const [draftPassword, setDraftPassword] = useState(() => localStorage.getItem(ADMIN_PASSWORD_KEY) || "");
  const [images, setImages] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");
  const [authError, setAuthError] = useState("");
  const [activeImageId, setActiveImageId] = useState("");
  const [editDrafts, setEditDrafts] = useState({});
  const [editingImageId, setEditingImageId] = useState("");

  const requestHeaders = useMemo(
    () => ({
      "x-gallery-admin-password": password,
    }),
    [password]
  );

  const loadImages = useCallback(async () => {
    if (!password) {
      return;
    }

    setIsLoading(true);
    setError("");

    try {
      const requestUrl = apiUrl("/api/gallery/admin/images");
      console.log("[gallery-admin] loading images", { requestUrl });
      const response = await fetch(requestUrl, {
        headers: requestHeaders,
      });
      const result = await response.json().catch(() => ({}));
      console.log("[gallery-admin] images response", {
        ok: response.ok,
        status: response.status,
        success: result?.success,
        count: result?.images?.length || 0,
        ids: (result?.images || []).map((image) => image.id),
        items: (result?.images || []).map((image) => ({
          id: image.id,
          status: image.status,
          mediaType: image.mediaType,
          originalFilename: image.originalFilename,
        })),
      });
      if (response.status === 401) {
        throw new Error("auth");
      }
      if (!response.ok || !result.success) {
        throw new Error(result.detail || result.message || t("galleryModeration.loadError"));
      }
      const nextImages = result.images || [];
      setImages(nextImages);
      setEditDrafts(
        Object.fromEntries(
          nextImages.map((image) => [
            image.id,
            {
              rotation: image.edits?.rotation ?? 0,
              zoom: image.edits?.zoom ?? 1,
              focusX: image.edits?.focusX ?? 0.5,
              focusY: image.edits?.focusY ?? 0.5,
            },
          ])
        )
      );
      setAuthError("");
    } catch (loadError) {
      if (loadError.message === "auth") {
        setPassword("");
        localStorage.removeItem(ADMIN_PASSWORD_KEY);
        setAuthError(t("galleryModeration.invalidPassword"));
      } else {
        setError(loadError.message || t("galleryModeration.loadError"));
      }
    } finally {
      setIsLoading(false);
    }
  }, [password, requestHeaders, t]);

  useEffect(() => {
    loadImages();
  }, [loadImages]);

  const handleLogin = async (event) => {
    event.preventDefault();
    localStorage.setItem(ADMIN_PASSWORD_KEY, draftPassword);
    setPassword(draftPassword);
  };

  const handleLogout = () => {
    localStorage.removeItem(ADMIN_PASSWORD_KEY);
    setPassword("");
    setDraftPassword("");
    setImages([]);
    setError("");
    setAuthError("");
  };

  const mutateImage = async (imageId, method, pathSuffix = "", body = null) => {
    setActiveImageId(imageId);
    setError("");

    try {
      const response = await fetch(apiUrl(`/api/gallery/admin/images/${imageId}${pathSuffix}`), {
        method,
        headers: body
          ? {
              ...requestHeaders,
              "content-type": "application/json",
            }
          : requestHeaders,
        body: body ? JSON.stringify(body) : undefined,
      });
      const result = await response.json().catch(() => ({}));
      if (response.status === 401) {
        throw new Error("auth");
      }
      if (!response.ok || !result.success) {
        throw new Error(result.detail || result.message || t("galleryModeration.actionError"));
      }
      await loadImages();
    } catch (actionError) {
      if (actionError.message === "auth") {
        handleLogout();
        setAuthError(t("galleryModeration.invalidPassword"));
      } else {
        setError(actionError.message || t("galleryModeration.actionError"));
      }
    } finally {
      setActiveImageId("");
    }
  };

  const updateEditDraft = (imageId, field, value) => {
    setEditDrafts((current) => ({
      ...current,
      [imageId]: {
        ...(current[imageId] || { rotation: 0, zoom: 1, focusX: 0.5, focusY: 0.5 }),
        [field]: value,
      },
    }));
  };

  const saveImageEdits = async (imageId) => {
    const draft = editDrafts[imageId] || { rotation: 0, zoom: 1, focusX: 0.5, focusY: 0.5 };
    await mutateImage(imageId, "POST", "/edits", draft);
  };

  const downloadOriginal = async (image) => {
    setActiveImageId(image.id);
    setError("");

    try {
      const requestUrl = image.originalDownloadUrl?.startsWith("http")
        ? image.originalDownloadUrl
        : apiUrl(image.originalDownloadUrl || `/api/gallery/admin/images/${image.id}/original`);
      const response = await fetch(requestUrl, {
        headers: requestHeaders,
      });
      if (response.status === 401) {
        throw new Error("auth");
      }
      if (!response.ok) {
        const result = await response.json().catch(() => ({}));
        throw new Error(result.detail || result.message || t("galleryModeration.downloadOriginalError"));
      }

      const blob = await response.blob();
      const blobUrl = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = blobUrl;
      link.download = image.originalFilename || image.storedFilename || `${image.id}`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(blobUrl);
    } catch (downloadError) {
      if (downloadError.message === "auth") {
        handleLogout();
        setAuthError(t("galleryModeration.invalidPassword"));
      } else {
        setError(downloadError.message || t("galleryModeration.downloadOriginalError"));
      }
    } finally {
      setActiveImageId("");
    }
  };

  const downloadAllMedia = async () => {
    const bulkActionId = "__download-all-media__";
    setActiveImageId(bulkActionId);
    setError("");

    try {
      const response = await fetch(apiUrl("/api/gallery/admin/media.zip"), {
        headers: requestHeaders,
      });
      if (response.status === 401) {
        throw new Error("auth");
      }
      if (!response.ok) {
        const result = await response.json().catch(() => ({}));
        throw new Error(result.detail || result.message || t("galleryModeration.downloadAllError"));
      }

      const blob = await response.blob();
      const blobUrl = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      const contentDisposition = response.headers.get("content-disposition") || "";
      const filenameMatch = contentDisposition.match(/filename="([^"]+)"/i);
      link.href = blobUrl;
      link.download = filenameMatch?.[1] || "wedding-gallery-media.zip";
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(blobUrl);
    } catch (downloadError) {
      if (downloadError.message === "auth") {
        handleLogout();
        setAuthError(t("galleryModeration.invalidPassword"));
      } else {
        setError(downloadError.message || t("galleryModeration.downloadAllError"));
      }
    } finally {
      setActiveImageId("");
    }
  };

  const resetImageEdits = (imageId) => {
    setEditDrafts((current) => ({
      ...current,
      [imageId]: { rotation: 0, zoom: 1, focusX: 0.5, focusY: 0.5 },
    }));
  };

  const groupedImages = {
    pending: images.filter((image) => image.status === "pending"),
    approved: images.filter((image) => image.status === "approved"),
    rejected: images.filter((image) => image.status === "rejected"),
  };

  useEffect(() => {
    console.log("[gallery-admin] grouped images", {
      total: images.length,
      pendingIds: groupedImages.pending.map((image) => image.id),
      approvedIds: groupedImages.approved.map((image) => image.id),
      rejectedIds: groupedImages.rejected.map((image) => image.id),
    });
  }, [groupedImages.approved, groupedImages.pending, groupedImages.rejected, images.length]);

  const getPreviewStyle = (imageId) => {
    const draft = editDrafts[imageId] || { rotation: 0, zoom: 1, focusX: 0.5, focusY: 0.5 };
    return {
      transform: `scale(${draft.zoom}) rotate(${draft.rotation}deg)`,
      transformOrigin: `${draft.focusX * 100}% ${draft.focusY * 100}%`,
    };
  };

  const editingImage = editingImageId ? images.find((image) => image.id === editingImageId) : null;

  return (
    <div className="gallery-admin-page">
      <div className="gallery-admin-topbar">
        <div>
          <h1 className="gallery-admin-title">{t("galleryModeration.title")}</h1>
          <p className="gallery-admin-subtitle">{t("galleryModeration.subtitle")}</p>
        </div>
        <LanguageSwitcher />
      </div>

      {!password ? (
        <form className="gallery-admin-login" onSubmit={handleLogin}>
          <label htmlFor="gallery-admin-password">{t("galleryModeration.passwordLabel")}</label>
          <input
            id="gallery-admin-password"
            type="password"
            value={draftPassword}
            onChange={(event) => setDraftPassword(event.target.value)}
            placeholder={t("galleryModeration.passwordPlaceholder")}
          />
          <button type="submit">{t("galleryModeration.login")}</button>
          {authError && <p className="gallery-admin-error">{authError}</p>}
        </form>
      ) : (
        <>
          <div className="gallery-admin-toolbar">
            <button type="button" onClick={loadImages}>
              {t("galleryModeration.refresh")}
            </button>
            <button
              type="button"
              className="secondary"
              onClick={downloadAllMedia}
              disabled={activeImageId === "__download-all-media__" || images.length === 0}
            >
              {t("galleryModeration.downloadAll")}
            </button>
            <button type="button" className="secondary" onClick={handleLogout}>
              {t("galleryModeration.logout")}
            </button>
          </div>

          {error && <p className="gallery-admin-error">{error}</p>}
          {isLoading ? <p className="gallery-admin-info">{t("galleryModeration.loading")}</p> : null}

          {["pending", "approved", "rejected"].map((status) => (
            <section key={status} className="gallery-admin-section">
              <h2>{t(`galleryModeration.sections.${status}`)}</h2>
              {groupedImages[status].length === 0 ? (
                <p className="gallery-admin-info">{t("galleryModeration.emptySection")}</p>
              ) : (
                <div className="gallery-admin-grid">
                  {groupedImages[status].map((image) => (
                    <article key={image.id} className="gallery-admin-card">
                      <div className="gallery-admin-image-shell">
                        <img
                          src={image.imageVariants?.medium || image.thumbnailUrl || image.url}
                          alt={image.originalFilename}
                          className="gallery-admin-image"
                          style={image.mediaType === "image" ? getPreviewStyle(image.id) : undefined}
                          loading="lazy"
                          decoding="async"
                        />
                      </div>
                      <div className="gallery-admin-card-body">
                        <p className="gallery-admin-filename">{image.originalFilename}</p>
                        <p>{t("galleryModeration.type")} {t(`galleryModeration.types.${image.mediaType}`)}</p>
                        <p>{t("galleryModeration.uploadedAt")} {image.uploadedAt}</p>
                        <p>{t("galleryModeration.status")} {t(`galleryModeration.statuses.${image.status}`)}</p>
                        {image.moderatedAt ? (
                          <p>{t("galleryModeration.moderatedAt")} {image.moderatedAt}</p>
                        ) : null}
                      </div>
                      <div className="gallery-admin-actions">
                        {image.mediaType === "image" ? (
                          <button
                            type="button"
                            className="secondary"
                            onClick={() => setEditingImageId(image.id)}
                            disabled={activeImageId === image.id}
                          >
                            Edit
                          </button>
                        ) : null}
                        <button
                          type="button"
                          className="secondary"
                          onClick={() => downloadOriginal(image)}
                          disabled={activeImageId === image.id}
                        >
                          {t("galleryModeration.downloadOriginal")}
                        </button>
                        {image.status !== "approved" ? (
                          <button
                            type="button"
                            onClick={() => mutateImage(image.id, "POST", "/approve")}
                            disabled={activeImageId === image.id}
                          >
                            {t("galleryModeration.approve")}
                          </button>
                        ) : null}
                        {image.status !== "rejected" ? (
                          <button
                            type="button"
                            className="secondary"
                            onClick={() => mutateImage(image.id, "POST", "/reject")}
                            disabled={activeImageId === image.id}
                          >
                            {t("galleryModeration.reject")}
                          </button>
                        ) : null}
                        <button
                          type="button"
                          className="danger"
                          onClick={() => mutateImage(image.id, "DELETE")}
                          disabled={activeImageId === image.id}
                        >
                          {t("galleryModeration.delete")}
                        </button>
                      </div>
                    </article>
                  ))}
                </div>
              )}
            </section>
          ))}
        </>
      )}

      {editingImage ? (
        <div className="gallery-admin-editor-backdrop" onClick={() => setEditingImageId("")}>
          <div className="gallery-admin-editor-modal" onClick={(event) => event.stopPropagation()}>
            <button
              type="button"
              className="gallery-admin-editor-close"
              onClick={() => setEditingImageId("")}
              aria-label="Close editor"
            >
              <X size={18} strokeWidth={2.25} />
            </button>
            <div className="gallery-admin-editor-preview-shell">
              <div className="gallery-admin-editor-preview-frame">
                <img
                  src={editingImage.imageVariants?.medium || editingImage.thumbnailUrl || editingImage.url}
                  alt={editingImage.originalFilename}
                  className="gallery-admin-editor-preview-image"
                  style={getPreviewStyle(editingImage.id)}
                />
              </div>
            </div>
            <div className="gallery-admin-edit-panel gallery-admin-edit-panel-modal">
              <h3 className="gallery-admin-editor-title">{editingImage.originalFilename}</h3>
              <label>
                <span>Rotate</span>
                <input
                  type="range"
                  min="-180"
                  max="180"
                  step="1"
                  value={editDrafts[editingImage.id]?.rotation ?? 0}
                  onChange={(event) => updateEditDraft(editingImage.id, "rotation", Number(event.target.value))}
                />
              </label>
              <label>
                <span>Crop Zoom</span>
                <input
                  type="range"
                  min="1"
                  max="3"
                  step="0.05"
                  value={editDrafts[editingImage.id]?.zoom ?? 1}
                  onChange={(event) => updateEditDraft(editingImage.id, "zoom", Number(event.target.value))}
                />
              </label>
              <label>
                <span>Crop X</span>
                <input
                  type="range"
                  min="0"
                  max="1"
                  step="0.01"
                  value={editDrafts[editingImage.id]?.focusX ?? 0.5}
                  onChange={(event) => updateEditDraft(editingImage.id, "focusX", Number(event.target.value))}
                />
              </label>
              <label>
                <span>Crop Y</span>
                <input
                  type="range"
                  min="0"
                  max="1"
                  step="0.01"
                  value={editDrafts[editingImage.id]?.focusY ?? 0.5}
                  onChange={(event) => updateEditDraft(editingImage.id, "focusY", Number(event.target.value))}
                />
              </label>
              <div className="gallery-admin-edit-actions">
                <button
                  type="button"
                  className="secondary"
                  onClick={() => resetImageEdits(editingImage.id)}
                  disabled={activeImageId === editingImage.id}
                >
                  Reset Edits
                </button>
                <button
                  type="button"
                  onClick={async () => {
                    await saveImageEdits(editingImage.id);
                    setEditingImageId("");
                  }}
                  disabled={activeImageId === editingImage.id}
                >
                  Save Edits
                </button>
              </div>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}

export default GalleryModeration;
